// @flow

import React, { useEffect, useState } from "react";
import style from "./style.module.scss";
import { t } from "ttag";
import {
    Filter,
    Icon,
    ChevronRight,
    Adjustment,
    FilterDrawer,
    Logo,
} from "@2po-c21/components";
import {
    addFilterFactory,
    Bedrooms,
    Condition,
    Elevator,
    Floors,
    Garden,
    ListingType,
    Location,
    Parking,
    Pool,
    Price,
    SubType,
    SurfaceHabitable,
    SurfaceGarden,
    SurfaceTotal,
    Terrace,
    Type,
    Country,
} from "@components/Filters";
import {
    type PropertyOverviewFilter,
    type PropertyFilterFacets,
} from "@utils/search";

type Props = {
    currentFilter: ?PropertyOverviewFilter,
    facets: ?PropertyFilterFacets,
    onFilterChanged?: (filter: *) => void,
    onFilterCleared?: () => void,
    language?: string,
    filterType?: string,
    countryFilterWhitelist?: string[],
};

const PropertyFilter = ({
    onFilterChanged,
    onFilterCleared,
    currentFilter,
    facets,
    language,
    filterType,
}: Props) => {
    const [filter, setFilter] = useState(undefined);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [tempFilter, setTempFilter] = useState(undefined);

    const toggleAdvancedFilters = () =>
        setShowAdvancedFilters(!showAdvancedFilters);

    const onChange = (newFilter) => {
        if (!showAdvancedFilters) {
            if (newFilter === filter) return;
            setFilter(newFilter);
        } else {
            setTempFilter(newFilter);
        }
    };

    const handleApplyFilters = () => {
        setFilter(tempFilter);
        setShowAdvancedFilters(false);
    };

    const handleDrawerClear = () => {
        setTempFilter({});
        setFilter({});
        setShowAdvancedFilters(false);
    };

    const handleDrawerClose = () => {
        setTempFilter(filter);
        setShowAdvancedFilters(false);
    };

    const onClear = () => onFilterCleared && onFilterCleared();

    useEffect(() => {
        if (!filter) return;

        onFilterChanged && onFilterChanged(filter);
    }, [filter]);

    useEffect(() => {}, [currentFilter]);

    useEffect(() => {
        if (showAdvancedFilters) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        // Cleanup function to restore scroll when component unmounts
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [showAdvancedFilters]);

    const amountOfAppliedFilters = () => {
        const defaultFiltersKeys = [
            "type",
            "listingType",
            "price",
            "location",
            "agency",
            "agencies",
            "related",
        ];
        const appliedFiltersLength =
            currentFilter &&
            Object.keys(currentFilter).filter(
                (filterKey) => !defaultFiltersKeys.includes(filterKey),
            ).length;

        return appliedFiltersLength ? `(${appliedFiltersLength})` : "";
    };

    return (
        <>
            <Filter
                onChange={onChange}
                onClear={onClear}
                initialValue={currentFilter}
                filterType={filterType}
            >
                {(applyFilter) => {
                    const addFilter = addFilterFactory(
                        showAdvancedFilters ? tempFilter : currentFilter,
                        facets,
                        applyFilter,
                    );

                    return (
                        <>
                            <div className={style.filters}>
                                {addFilter(
                                    <Location
                                        language={language}
                                        additionalClassName={
                                            style.propertyFilterLocation
                                        }
                                    />,
                                )}
                                {addFilter(<ListingType />)}
                                {addFilter(<Type />)}
                                {addFilter(<Price />)}

                                <FilterDrawer
                                    isOpen={showAdvancedFilters}
                                    onClose={handleDrawerClose}
                                    onApply={handleApplyFilters}
                                    onClear={handleDrawerClear}
                                    title={t`common.filters.more-filters`}
                                    clearBtnText={t`common.filters.clear`}
                                    applyBtnText={t`common.filters.apply`}
                                >
                                    <div className={style.filterDrawerContent}>
                                        <div
                                            className={
                                                style.filterDrawerContentSection
                                            }
                                        >
                                            <div
                                                className={
                                                    style.filterDrawerContentSectionGrid
                                                }
                                            >
                                                {addFilter(
                                                    <SubType
                                                        fullWidth
                                                        showSelectedInLabel={
                                                            false
                                                        }
                                                    />,
                                                )}
                                                {addFilter(
                                                    <Floors fullWidth />,
                                                )}
                                                {addFilter(
                                                    <Condition
                                                        fullWidth
                                                        showSelectedInLabel={
                                                            false
                                                        }
                                                    />,
                                                )}
                                                {addFilter(
                                                    <Country
                                                        fullWidth
                                                        showSelectedInLabel={
                                                            false
                                                        }
                                                    />,
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                (style.filterDrawerContentRowSection,
                                                style.filterDrawerSectionSliders)
                                            }
                                        >
                                            {addFilter(<Bedrooms />)}
                                            {addFilter(<SurfaceHabitable />)}
                                            {addFilter(<SurfaceTotal />)}
                                            {addFilter(<SurfaceGarden />)}
                                        </div>
                                        <div
                                            className={
                                                style.filterDrawerContentSection
                                            }
                                        >
                                            <span
                                                style={{
                                                    marginBottom: "0.5rem",
                                                    display: "block",
                                                }}
                                            >
                                                {t`common.filters.accomodations`}
                                            </span>
                                            <div
                                                className={
                                                    style.filterDrawerSectionBooleans
                                                }
                                            >
                                                {addFilter(<Garden />)}
                                                {addFilter(<Terrace />)}
                                                {addFilter(<Pool />)}
                                                {addFilter(<Parking />)}
                                                {addFilter(<Elevator />)}
                                            </div>
                                        </div>
                                    </div>
                                </FilterDrawer>
                                <a
                                    className={style.filterButton}
                                    onClick={() => toggleAdvancedFilters()}
                                >
                                    <span className={style.filterButtonText}>
                                        <Icon icon={Adjustment} rotation={90} />
                                        {`${t`common.filters.more-filters`} ${amountOfAppliedFilters()}`}
                                        <Icon icon={ChevronRight} size="xs" />
                                    </span>
                                </a>
                                <div className={style.logoContainer}>
                                    <div className={style.logo}>
                                        <Logo logo={"seal"} width="80" />
                                    </div>
                                </div>
                            </div>
                        </>
                    );
                }}
            </Filter>
        </>
    );
};

export default PropertyFilter;
